<template>
  <div class="personal-info-page">
    <!-- Warning Banner (from register) -->
    <div v-if="showRegisterWarning" class="warning-banner">
      <span class="warning-icon">⚠️</span>
      <span class="warning-text"
        >กรุณาตรวจสอบข้อมูลของท่านก่อนเข้าเล่นเกมส์ หากไม่ถูกต้องสามารถกดปุ่ม
        'แก้ไขข้อมูลบัญชี'</span
      >
    </div>

    <!-- Personal Info Card -->
    <div class="info-card">
      <!-- Header -->
      <div class="card-header">
        <span class="header-icon">👤</span>
        <span class="header-title">ข้อมูลส่วนตัว</span>
      </div>

      <!-- Card Body -->
      <div class="card-body">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <span>กำลังโหลด...</span>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <span class="error-icon">⚠️</span>
          <span class="error-text">ไม่สามารถโหลดข้อมูลได้</span>
          <button class="retry-btn" @click="fetchPersonalInfo">
            ลองใหม่
          </button>
        </div>

        <!-- Personal Info Display -->
        <div v-else class="name-card">
          <!-- Info Grid - 2 Columns (View Mode) -->
          <template v-if="!isEditing">
            <div class="info-grid">
              <div class="info-box">
                <span class="box-icon">📝</span>
                <div class="box-content">
                  <span class="box-label">ชื่อจริง</span>
                  <span class="box-value">{{
                    personalInfo.firstname || '-'
                  }}</span>
                </div>
              </div>

              <div class="info-box">
                <span class="box-icon">📝</span>
                <div class="box-content">
                  <span class="box-label">นามสกุล</span>
                  <span class="box-value">{{
                    personalInfo.lastname || '-'
                  }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- Info Grid - Edit Mode -->
          <template v-else>
            <div class="edit-form name-edit-form">
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">ชื่อจริง</label>
                  <input
                    v-model="editForm.firstname"
                    type="text"
                    placeholder="กรอกชื่อจริง"
                    class="form-input disabled"
                    disabled
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">นามสกุล</label>
                  <input
                    v-model="editForm.lastname"
                    type="text"
                    placeholder="กรอกนามสกุล"
                    class="form-input disabled"
                    disabled
                  />
                </div>
              </div>
            </div>
          </template>

          <!-- Password Section (hide when editing) -->
          <div v-if="!isEditing" class="password-section">
            <div class="password-box">
              <span class="box-icon">🔐</span>
              <div class="box-content">
                <span class="box-label">รหัสผ่าน</span>
                <span class="box-value">{{ displayPassword }}</span>
              </div>
              <button class="toggle-btn" @click="togglePasswordVisibility">
                <feather-icon :icon="passwordToggleIcon" size="16" />
              </button>
            </div>
          </div>

          <!-- Bank Info Section -->
          <div class="bank-section">
            <div class="section-title">
              <span class="title-icon">🏦</span>
              <span class="title-text">ข้อมูลบัญชีธนาคาร</span>
            </div>

            <!-- View Mode -->
            <template v-if="!isEditing">
              <div class="bank-card">
                <div class="bank-row">
                  <span class="bank-label">ชื่อบัญชี</span>
                  <span class="bank-value">{{
                    personalInfo.name_th || '-'
                  }}</span>
                </div>
                <div class="bank-row">
                  <span class="bank-label">เลขที่บัญชี</span>
                  <span class="bank-value account-number">{{
                    formattedBankNo
                  }}</span>
                </div>
              </div>
              <button class="edit-btn" @click="startEdit">
                <feather-icon icon="EditIcon" size="14" />
                <span>แก้ไขข้อมูลบัญชี</span>
              </button>
            </template>

            <!-- Edit Mode -->
            <template v-else>
              <div class="edit-form">
                <div class="form-group">
                  <label class="form-label">ธนาคาร</label>
                  <v-select
                    v-model="editForm.bank"
                    :options="banks"
                    label="text"
                    placeholder="เลือกธนาคาร"
                    class="bank-select"
                    append-to-body
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">เลขที่บัญชี</label>
                  <input
                    v-model="editForm.accountNo"
                    type="text"
                    maxlength="12"
                    placeholder="กรอกเลขบัญชี 10-12 หลัก"
                    class="form-input"
                    :class="{
                      'is-invalid': editForm.accountNo && !isAccountNoValid
                    }"
                    @input="
                      editForm.accountNo = editForm.accountNo.replace(/\D/g, '')
                    "
                  />
                  <span
                    v-if="editForm.accountNo && !isAccountNoValid"
                    class="error-hint"
                  >
                    กรุณากรอกเลขบัญชี 10-12 หลัก
                  </span>
                </div>
                <div class="form-actions">
                  <button
                    class="cancel-btn"
                    :disabled="saving"
                    @click="cancelEdit"
                  >
                    ยกเลิก
                  </button>
                  <button
                    class="save-btn"
                    :disabled="!canSave"
                    @click="saveEdit"
                  >
                    <span v-if="saving" class="btn-spinner"></span>
                    <span>{{ saving ? 'กำลังบันทึก...' : 'บันทึก' }}</span>
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'PersonalInfoPage',
  components: {
    vSelect
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      loading: false,
      error: null,
      personalInfo: {
        firstname: '',
        lastname: '',
        login_password: '',
        bank_id: null,
        bank_no: '',
        name_th: ''
      },
      isEditing: false,
      saving: false,
      editForm: {
        firstname: '',
        lastname: '',
        bank: null,
        accountNo: ''
      },
      banks: [
        { value: '1', text: 'ธนาคารกรุงเทพ' },
        { value: '2', text: 'ธนาคารกรุงศรีอยุธยา' },
        { value: '3', text: 'ธนาคารกรุงไทย' },
        { value: '4', text: 'ธนาคารกสิกรไทย' },
        { value: '5', text: 'ธนาคารไทยพาณิชย์' },
        { value: '22', text: 'ธนาคารไทยธนชาติ' },
        { value: '16', text: 'ธนาคารเกียรตินาคิน' },
        { value: '7', text: 'ธนาคารออมสิน' },
        { value: '10', text: 'ธนาคารยูโอบี' },
        { value: '9', text: 'ธนาคารเพื่อการเกษตร' },
        { value: '12', text: 'ธนาคารอาคารสงเคราะห์' },
        { value: '14', text: 'ธนาคารทิสโก้' },
        { value: '15', text: 'ธนาคารซิตี้แบงก์' },
        { value: '18', text: 'ธนาคารแลนด์ แอนด์ เฮ้าส์' },
        { value: '11', text: 'ธนาคารซีไอเอ็มบี' }
      ]
    }
  },
  computed: {
    showRegisterWarning() {
      return this.$route.query.from === 'register'
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    displayPassword() {
      if (this.passwordFieldType === 'password') {
        return '••••••••'
      }
      return this.personalInfo.login_password || '-'
    },
    formattedBankNo() {
      const bankNo = this.personalInfo.bank_no || ''
      if (!bankNo) return '-'
      // Format: XXX-X-XXXXX-X
      return bankNo.replace(/(\d{3})(\d{1})(\d{5})(\d{1})/, '$1-$2-$3-$4')
    },
    isAccountNoValid() {
      const len = this.editForm.accountNo.length
      return len >= 10 && len <= 12 && /^\d+$/.test(this.editForm.accountNo)
    },
    hasChanges() {
      const originalBankId = String(this.personalInfo.bank_id || '')
      const originalAccountNo = this.personalInfo.bank_no || ''

      return (
        originalBankId !== (this.editForm.bank?.value || '') ||
        originalAccountNo !== this.editForm.accountNo
      )
    },
    canSave() {
      return (
        this.editForm.bank &&
        this.isAccountNoValid &&
        !this.saving &&
        this.hasChanges
      )
    }
  },
  mounted() {
    this.fetchPersonalInfo()
  },
  methods: {
    async fetchPersonalInfo() {
      try {
        this.loading = true
        this.error = null
        const response = await this.$http.get('/member/personal/info')

        if (response.data.success) {
          this.personalInfo = response.data.member
        } else {
          throw new Error('Failed to fetch personal info')
        }
      } catch (err) {
        this.error = err
        console.error('Failed to fetch personal info:', err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'ไม่สามารถโหลดข้อมูลได้',
            icon: 'AlertTriangleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.loading = false
      }
    },
    startEdit() {
      this.editForm.firstname = this.personalInfo.firstname || ''
      this.editForm.lastname = this.personalInfo.lastname || ''
      this.editForm.bank =
        this.banks.find(b => b.value === String(this.personalInfo.bank_id)) ||
        null
      this.editForm.accountNo = this.personalInfo.bank_no || ''
      this.isEditing = true
    },
    cancelEdit() {
      this.isEditing = false
      this.editForm = { firstname: '', lastname: '', bank: null, accountNo: '' }
    },
    async saveEdit() {
      if (!this.canSave) return
      this.saving = true
      try {
        const response = await this.$http.put('/member/account', {
          firstname: this.personalInfo.firstname,
          lastname: this.personalInfo.lastname,
          bank_id: this.editForm.bank.value,
          bank_no: this.editForm.accountNo
        })

        if (response.data.status) {
          this.isEditing = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'บันทึกข้อมูลสำเร็จ',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
          await this.fetchPersonalInfo()
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: response.data.error,
              icon: 'AlertTriangleIcon',
              variant: 'danger'
            }
          })
        }
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: err.response?.data?.message || 'ไม่สามารถบันทึกข้อมูลได้',
            icon: 'AlertTriangleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Mobile-First styles (375px base)
.personal-info-page {
  min-height: 100vh;
  padding: 16px 12px;
  padding-bottom: 100px;

  @media (min-width: 768px) {
    padding: 24px 20px;
    padding-bottom: 120px;
  }
}

// Warning Banner
.warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 12px;

  @media (min-width: 768px) {
    padding: 14px 16px;
    margin-bottom: 20px;
  }
}

.warning-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.warning-text {
  color: #fbbf24;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.5;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
}

// Info Card
.info-card {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) {
    border-radius: 20px;
  }
}

// Card Header
.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) {
    padding: 16px 20px;
  }
}

.header-icon {
  font-size: 1.2rem;

  @media (min-width: 768px) {
    font-size: 1.4rem;
  }
}

.header-title {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
}

// Card Body
.card-body {
  padding: 12px;

  @media (min-width: 768px) {
    padding: 16px;
  }
}

// Name Card Container
.name-card {
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (min-width: 768px) {
    gap: 12px;
  }
}

// Info Grid - 2 Columns
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  @media (min-width: 768px) {
    gap: 10px;
  }
}

.info-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);

  @media (min-width: 768px) {
    padding: 12px;
  }
}

.box-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.box-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.box-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.65rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  @media (min-width: 768px) {
    font-size: 0.7rem;
  }
}

.box-value {
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
}

// Password Section
.password-section {
  .password-box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.06);

    @media (min-width: 768px) {
      padding: 12px;
    }

    .box-content {
      flex: 1;
    }

    .box-value {
      font-family: monospace;
      letter-spacing: 2px;
    }
  }
}

.toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(115, 103, 240, 0.15);
  border: 1px solid rgba(115, 103, 240, 0.3);
  border-radius: 8px;
  color: #9e95f5;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(115, 103, 240, 0.25);
    border-color: rgba(115, 103, 240, 0.5);
    color: #fff;
  }

  &:active {
    transform: scale(0.95);
  }
}

// Bank Section
.bank-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.title-icon {
  font-size: 0.9rem;
}

.title-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.bank-card {
  background: linear-gradient(
    135deg,
    rgba(74, 222, 128, 0.1),
    rgba(34, 197, 94, 0.05)
  );
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: 768px) {
    padding: 12px 14px;
  }
}

.bank-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.bank-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  font-weight: 500;
}

.bank-value {
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: right;

  &.account-number {
    font-family: monospace;
    letter-spacing: 1px;
    color: #4ade80;
  }
}

// Edit Button
.edit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 10px;
  padding: 10px 16px;
  background: rgba(115, 103, 240, 0.15);
  border: 1px solid rgba(115, 103, 240, 0.3);
  border-radius: 8px;
  color: #9e95f5;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(115, 103, 240, 0.25);
    border-color: rgba(115, 103, 240, 0.5);
  }

  &:active {
    transform: scale(0.98);
  }
}

// Edit Form
.edit-form {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 14px;
}

.form-group {
  margin-bottom: 12px;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.9rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(115, 103, 240, 0.5);
  }

  &.is-invalid {
    border-color: #ef4444;
  }

  &.disabled,
  &:disabled {
    background: rgba(0, 0, 0, 0.4);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
    border-color: rgba(255, 255, 255, 0.08);
  }
}

.error-hint {
  display: block;
  color: #ef4444;
  font-size: 0.7rem;
  margin-top: 4px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.name-edit-form {
  margin-bottom: 10px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}

.cancel-btn,
.save-btn {
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.save-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--theme-gradient-primary);
  border: none;
  color: #fff;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--theme-primary-rgb), 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

// Loading State
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 30px 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(var(--theme-primary-rgb), 0.3);
  border-top-color: var(--theme-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Error State
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 30px 0;
}

.error-icon {
  font-size: 1.5rem;
}

.error-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.retry-btn {
  padding: 8px 20px;
  background: rgba(var(--theme-primary-rgb), 0.2);
  border: 1px solid rgba(var(--theme-primary-rgb), 0.4);
  border-radius: 8px;
  color: var(--theme-primary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--theme-primary-rgb), 0.3);
    border-color: rgba(var(--theme-primary-rgb), 0.6);
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// Vue-select dark theme override for PersonalInfo page
.personal-info-page {
  .bank-select {
    .vs__dropdown-toggle {
      background: rgba(0, 0, 0, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 8px;
      padding: 8px 10px;
      min-height: 42px;
    }

    .vs__selected {
      color: #fff;
      margin: 0;
      padding: 0;
    }

    .vs__search {
      color: #fff;
      margin: 0;
      padding: 0;

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }
    }

    .vs__clear,
    .vs__open-indicator {
      fill: rgba(255, 255, 255, 0.5);
    }

    .vs__actions {
      padding-top: 0;
    }

    .vs__selected-options {
      padding: 0;
    }
  }
}

// Dropdown menu styles (appended to body, so needs global scope)
.vs__dropdown-menu {
  background: #1e1e2d !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;
  margin-top: 4px;
  padding: 4px 0 !important;
  z-index: 9999 !important;

  .vs__dropdown-option {
    color: #fff;
    padding: 10px 12px;

    &--highlight {
      background: rgba(115, 103, 240, 0.3);
    }
  }
}
</style>
